/**
 * Native app bridge.
 *
 * When AkountFly runs inside its React Native / WebView wrapper, the wrapper
 * injects `window.NativeApp` BEFORE our bundle boots. We use it for:
 *   - real OS Face ID / Touch ID / Fingerprint prompts (Android WebView has
 *     no WebAuthn support, and iOS WKWebView needs an Associated-Domains
 *     entitlement we don't ship yet).
 *   - haptics, native share, opening OS settings.
 *
 * Contract from the mobile team (see NativeApp shape below). All methods are
 * best-effort - we always resolve safely so a broken bridge never crashes
 * the web app.
 *
 *   window.NativeApp = {
 *     isNative: true,
 *     platform: 'ios' | 'android',
 *     getBiometryInfo(): Promise<{available, enrolled, type, platform}>,
 *     biometricAuth(reason): Promise<{success, error?, available?, enrolled?, canAskAgain?}>,
 *     share(text, url), haptic(kind), openSettings(),
 *   }
 *
 * A `nativeapp-ready` event is dispatched on `window` once the bridge is
 * fully wired up (in some builds the object is populated a tick after our
 * bundle evaluates - `waitForNative()` handles that race).
 */

import { useEffect, useState } from 'react';

const NATIVE_READY_EVENT = 'nativeapp-ready';
const READY_WAIT_MS = 250; // give the wrapper a beat before deciding

/** Synchronous check. Returns true when running inside the native wrapper. */
export const isNativeApp = () =>
  typeof window !== 'undefined' && !!window.NativeApp?.isNative;

/**
 * Push notifications (mobile team, Oct 2026). Register this device for the
 * signed-in user — called after login and on every app open. Tokens are kept
 * by the mobile backend; we only pass the user id we later send pushes to.
 * Resolves { success } | { error: 'denied' | ... } | null (not native).
 */
export const registerPushForUser = async (userId) => {
  if (!userId || !(await waitForNative())) return null;
  try {
    const res = await window.NativeApp.registerPush(String(userId));
    if (res?.error === 'denied') console.info('[native] push permission declined by the user');
    return res || null;
  } catch (e) {
    console.warn('[native] registerPush failed', e);
    return { error: String(e?.message || e) };
  }
};

/** Keep the home-screen icon badge equal to the bell's unread count (0 clears it). */
export const setNativeBadge = async (count) => {
  if (!isNativeApp() || typeof window.NativeApp?.setBadge !== 'function') return;
  try { await window.NativeApp.setBadge(Math.max(0, Number(count) || 0)); } catch (_) { /* noop */ }
};

/** Get the platform string when native, else null. */
export const nativePlatform = () =>
  (typeof window !== 'undefined' && window.NativeApp?.platform) || null;

/** True only inside the native iOS app (App Store build). Gates every
 * Apple-compliance behaviour: sign-in-only welcome, hidden billing/fee UI,
 * neutral banners and the X-Client-Platform header. */
export const isIOSNative = () =>
  isNativeApp() && String(nativePlatform() || '').toLowerCase() === 'ios';

/**
 * Await the native bridge with a short grace window. If NativeApp is already
 * present we resolve immediately; otherwise we listen once for the
 * `nativeapp-ready` event, with a hard timeout so browsers don't hang.
 * Returns true when native, false when we're clearly in a browser.
 */
export const waitForNative = (timeoutMs = READY_WAIT_MS) =>
  new Promise((resolve) => {
    if (isNativeApp()) return resolve(true);
    let done = false;
    const finish = (v) => {
      if (done) return;
      done = true;
      window.removeEventListener(NATIVE_READY_EVENT, onReady);
      resolve(v);
    };
    const onReady = () => finish(isNativeApp());
    window.addEventListener(NATIVE_READY_EVENT, onReady, { once: true });
    setTimeout(() => finish(isNativeApp()), timeoutMs);
  });

/**
 * Probe device biometry capability via the bridge. Returns a normalized shape
 * with sensible defaults when the bridge is missing or throws.
 */
export const getBiometryInfo = async () => {
  if (!isNativeApp()) {
    return { available: false, enrolled: false, type: 'none', platform: null };
  }
  try {
    const info = await window.NativeApp.getBiometryInfo();
    return {
      available: !!info?.available,
      enrolled: !!info?.enrolled,
      type: info?.type || 'none',
      platform: info?.platform || nativePlatform(),
    };
  } catch {
    return { available: false, enrolled: false, type: 'none', platform: nativePlatform() };
  }
};

/**
 * Single-source-of-truth label for the device's biometric method. Used by
 * every biometric string in the app (buttons, headings, toasts, aria-labels,
 * and the `reason` passed into biometricAuth). Never hardcode "Face ID" /
 * "Touch ID" / "Fingerprint" - call this helper with the info returned by
 * `getBiometryInfo()`.
 *
 *   face        → "Face ID"        (iPhone/iPad Face ID, Android Face Unlock class 3)
 *   fingerprint → "Touch ID" on iOS/macOS, "Fingerprint" everywhere else
 *   iris        → "Iris"           (older Samsungs)
 *   anything else → "Biometrics"    (safe generic fallback)
 */
export const biometricLabel = (info) => {
  const type = info?.type;
  if (type === 'face') return 'Face ID';
  if (type === 'fingerprint') {
    return info?.platform === 'ios' ? 'Touch ID' : 'Fingerprint';
  }
  if (type === 'iris') return 'Iris';
  return 'Biometrics';
};

/**
 * Trigger the OS biometric prompt via the bridge. `reason` should be a short,
 * user-facing string (shown on iOS below the Face ID prompt).
 *
 * Always resolves to `{success, error?}` - never throws - so callers can wire
 * a clean fallback without a try/catch storm.
 */
export const nativeBiometricAuth = async (reason = 'Verify to continue') => {
  if (!isNativeApp()) {
    return { success: false, error: 'not_native' };
  }
  try {
    const res = await window.NativeApp.biometricAuth(reason);
    // Normalize: some bridges return { authenticated } instead of { success }.
    const success = !!(res && (res.success === true || res.authenticated === true));
    return {
      success,
      error: res?.error,
      available: res?.available,
      enrolled: res?.enrolled,
      canAskAgain: res?.canAskAgain,
    };
  } catch (e) {
    return { success: false, error: String(e?.message || e || 'bridge_error') };
  }
};

/** Light haptic tap through the bridge when available (silent no-op otherwise). */
export const nativeHaptic = (kind = 'light') => {
  try {
    if (isNativeApp() && typeof window.NativeApp.haptic === 'function') {
      window.NativeApp.haptic(kind);
    }
  } catch { /* no-op */ }
};

/**
 * Read the OS-level App Lock state through the bridge.
 *
 * Native contract:
 *   window.NativeApp.getAppLockState() -> { enabled, available, enrolled }
 *     - available: device has biometric hardware
 *     - enrolled:  user has set up biometric in device settings
 *     - enabled:   AkountFly App Lock is currently ON
 *
 * Returns a normalised shape - never throws - so callers can render
 * without try/catch storms. When the bridge is missing (older wrapper
 * versions), we return `available: false` so the UI hides the row.
 */
export const getAppLockState = async () => {
  if (!isNativeApp() || typeof window.NativeApp.getAppLockState !== 'function') {
    return { enabled: false, available: false, enrolled: false };
  }
  try {
    const s = await window.NativeApp.getAppLockState();
    return {
      enabled: !!s?.enabled,
      available: !!s?.available,
      enrolled: !!s?.enrolled,
    };
  } catch {
    return { enabled: false, available: false, enrolled: false };
  }
};

/**
 * Toggle App Lock. When enabling, the OS shows the biometric confirm
 * sheet; the native side only flips the flag on success.
 *
 * Returns `{success, enabled, error?}`. `success:false` means the user
 * cancelled or biometric failed - the UI must NOT reflect the intended
 * `next` value in that case; use `enabled` from the result.
 */
export const setAppLock = async (enabled) => {
  if (!isNativeApp() || typeof window.NativeApp.setAppLock !== 'function') {
    return { success: false, enabled: false, error: 'not_native' };
  }
  try {
    const r = await window.NativeApp.setAppLock(!!enabled);
    return {
      success: r?.success !== false, // native may omit `success` on happy path
      enabled: !!r?.enabled,
      error: r?.error,
    };
  } catch (e) {
    return { success: false, enabled: false, error: String(e?.message || e || 'bridge_error') };
  }
};

/**
 * Open a URL in the phone's default browser through the native bridge.
 * Used to route users to the web signup / trial flow because Apple and
 * Google forbid selling subscriptions inside a mobile app - the checkout
 * MUST happen in an external browser. Falls back to `window.open` when
 * the bridge is missing (e.g. new native build that doesn't ship this
 * method yet - still opens the URL correctly in-webview).
 */
export const openExternal = (url) => {
  try {
    if (isNativeApp() && typeof window.NativeApp.openExternal === 'function') {
      window.NativeApp.openExternal(url);
      return true;
    }
  } catch { /* fall through to window.open */ }
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  } catch {
    // Very old webviews: last-resort in-frame navigation.
    if (typeof window !== 'undefined') window.location.href = url;
    return false;
  }
};

/**
 * React hook that returns `true` when the app is running inside the native
 * wrapper. Handles the bridge landing slightly after mount by subscribing
 * once to the `nativeapp-ready` event.
 */
export function useIsNative() {
  const [native, setNative] = useState(isNativeApp);
  useEffect(() => {
    if (isNativeApp()) return undefined;
    const onReady = () => setNative(isNativeApp());
    window.addEventListener(NATIVE_READY_EVENT, onReady, { once: true });
    // Belt-and-braces: check again shortly in case we mounted between
    // NativeApp being populated and the event firing.
    const t = setTimeout(() => setNative(isNativeApp()), READY_WAIT_MS);
    return () => {
      window.removeEventListener(NATIVE_READY_EVENT, onReady);
      clearTimeout(t);
    };
  }, []);
  return native;
}
